import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import type { EnquiryPrefill } from "../../utils/packageEnquiry";
import { destinationEntries } from "../../content/indiaSiteContent";
import { submitEnquiry } from "../../services/enquiry";

const CTA: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const sectionRef = useRef<HTMLElement | null>(null);
  const destinationFieldRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [destinationQuery, setDestinationQuery] = useState("");
  const routeState = location.state as {
    enquiryPrefill?: EnquiryPrefill;
    openCustomItinerary?: boolean;
  } | null;
  const enquiryPrefill = routeState?.enquiryPrefill;
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    destination: "",
    budget: "",
    travelDate: "",
    numberOfDays: "",
    travellers: "",
    nationality: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const destinationOptions = useMemo(
    () =>
      destinationEntries
        .map((destination) => ({
          value: destination.name,
          label: destination.name,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [],
  );
  const filteredDestinationOptions = useMemo(() => {
    const query = destinationQuery.trim().toLowerCase();

    if (!query) {
      return destinationOptions;
    }

    return destinationOptions.filter((destination) =>
      destination.label.toLowerCase().includes(query),
    );
  }, [destinationOptions, destinationQuery]);

  useEffect(() => {
    if (
      !routeState?.openCustomItinerary &&
      !enquiryPrefill &&
      location.hash !== "#custom-itinerary"
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(true);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [enquiryPrefill, location.hash, routeState?.openCustomItinerary]);

  useEffect(() => {
    const prefillDestination = enquiryPrefill?.packageSummary?.destinationName;

    if (!prefillDestination) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      destination: prev.destination || prefillDestination,
    }));
    setDestinationQuery((prev) => prev || prefillDestination);
  }, [enquiryPrefill]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        destinationFieldRef.current &&
        !destinationFieldRef.current.contains(event.target as Node)
      ) {
        setDestinationOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDestinationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const budgetValue = form.budget ? Number(form.budget) : undefined;
      const numberOfDaysValue = form.numberOfDays ? Number(form.numberOfDays) : undefined;
      const travellersValue = form.travellers ? Number(form.travellers) : undefined;

      if (budgetValue !== undefined && (Number.isNaN(budgetValue) || budgetValue <= 0)) {
        throw new Error("Please enter a valid budget amount.");
      }
      if (numberOfDaysValue !== undefined && (Number.isNaN(numberOfDaysValue) || numberOfDaysValue <= 0)) {
        throw new Error("Please enter a valid number of days.");
      }
      if (travellersValue !== undefined && (Number.isNaN(travellersValue) || travellersValue <= 0)) {
        throw new Error("Please enter a valid number of travellers.");
      }

      if (
        !form.fullName.trim() ||
        !form.phone.trim() ||
        !form.email.trim() ||
        !form.destination.trim() ||
        !form.nationality.trim()
      ) {
        throw new Error("Please fill required fields.");
      }

      const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        destination: form.destination.trim(),
        nationality: form.nationality.trim(),
        travelDate: form.travelDate || undefined,
        numberOfDays: numberOfDaysValue,
        numberOfTravellers: travellersValue,
        budget: budgetValue,
        leadType: "HOLIDAY",
        source: "Website UAE - Destination Inquiry",
        sourcePage: "Destination",
        leadCountry: "UAE",
        country: "UAE",
        pagePath,
        pageUrl: window.location.href,
        utmSource: "website_uae",
        utmMedium: "contact_form",
        utmCampaign: "destination_inquiry",
        subject: enquiryPrefill?.subject || "destination_inquiry",
        message: enquiryPrefill?.message || "",
      };

      const result = await submitEnquiry(payload);

      if (!result.success) {
        throw new Error(result.message || "Failed to submit enquiry");
      }

      setSuccess("Enquiry submitted successfully!");
      setForm({
        fullName: "",
        phone: "",
        email: "",
        destination: "",
        budget: "",
        travelDate: "",
        numberOfDays: "",
        travellers: "",
        nationality: "",
      });
      setDestinationQuery("");
      setDestinationOpen(false);

      setTimeout(() => setOpen(false), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="custom-itinerary"
      ref={sectionRef}
      className="py-20 bg-brand-dark text-white relative overflow-hidden"
    >

      {/* Background Image */}
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2000&q=80')",
        }}
      />

      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">

        <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
          Can't decide where to go in India?
        </h2>

        <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
          Let our luxury travel concierges craft a bespoke India itinerary tailored
          perfectly to your desires and preferences.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">

          <button
            onClick={() => setOpen(true)}
            className="px-8 py-4 bg-brand-pink text-white rounded-xl font-medium hover:bg-brand-pink/90 transition-all shadow-lg shadow-brand-pink/20"
          >
            {t(" Get your Travel Quote")}
          </button>

          <a
            href="https://wa.me/971545838393"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-transparent border border-white/30 text-white rounded-xl font-medium hover:bg-white/10 transition-all"
          >
            {t("Contact an Expert")}
          </a>

        </div>
      </div>

      {/* POPUP */}
      {open && (
       <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 px-4 py-6 backdrop-blur-sm">
          <div className="relative max-h-[90vh] scrollbar-hide w-full max-w-md overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-brand-dark transition-colors"
            >
              <FaTimes size={18} />
            </button>

            <h3 className="font-heading text-2xl font-bold text-brand-dark mb-2">
              {t(" Get your Travel Quote")}
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              {t("Fill in the details and we'll craft your perfect Holidays.")}
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">
                  {t("Your Name")} <span className="text-brand-pink">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Pooja Sharma"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">
                  {t("Phone Number")} <span className="text-brand-pink">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="e.g. +91 50123 04567"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">
                  {t("Email")} <span className="text-brand-pink">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="e.g. pooja@example.com"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">
                  {t("Destination Country")} <span className="text-brand-pink">*</span>
                </label>
                <div ref={destinationFieldRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setDestinationOpen((value) => !value)}
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-brand-dark transition-colors focus:border-brand-pink focus:outline-none focus:ring-2 focus:ring-brand-pink/20"
                    aria-haspopup="listbox"
                    aria-expanded={destinationOpen}
                  >
                    <span className={form.destination ? "text-brand-dark" : "text-gray-400"}>
                      {form.destination || t("Select country")}
                    </span>
                    <FaChevronDown
                      className={`ml-3 text-xs text-gray-400 transition-transform ${
                        destinationOpen ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>

                  {destinationOpen && (
                    <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
                      <div className="border-b border-gray-100 p-3">
                        <input
                          type="text"
                          value={destinationQuery}
                          onChange={(event) => setDestinationQuery(event.target.value)}
                          placeholder={t("Search country")}
                          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-brand-dark placeholder:text-gray-400 focus:border-brand-pink focus:outline-none focus:ring-2 focus:ring-brand-pink/20"
                          autoFocus
                        />
                      </div>

                      <ul role="listbox" className="max-h-64 overflow-y-auto py-2">
                        {filteredDestinationOptions.length > 0 ? (
                          filteredDestinationOptions.map((destination) => (
                            <li key={destination.value}>
                              <button
                                type="button"
                                onClick={() => {
                                  setForm((prev) => ({
                                    ...prev,
                                    destination: destination.value,
                                  }));
                                  setDestinationQuery(destination.value);
                                  setDestinationOpen(false);
                                }}
                                className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition hover:bg-brand-pink/5 ${
                                  form.destination === destination.value
                                    ? "bg-brand-pink/10 text-brand-pink"
                                    : "text-brand-dark"
                                }`}
                              >
                                {destination.label}
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="px-4 py-3 text-sm text-gray-500">
                            {t("No destinations found")}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">
                  {t("Nationality")} <span className="text-brand-pink">*</span>
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={form.nationality}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Indian, UAE, British"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">{t("Travel Date")}</label>
                <input
                  type="date"
                  name="travelDate"
                  value={form.travelDate}
                  onChange={handleChange}
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">{t("No. of Days")}</label>
                <input
                  type="number"
                  name="numberOfDays"
                  value={form.numberOfDays}
                  onChange={handleChange}
                  min={1}
                  placeholder="e.g. 7"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">{t("Budget")}</label>
                <input
                  type="number"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  min={1}
                  placeholder="e.g. 15000"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-600">{t("Number of Travellers")}</label>
                <input
                  type="number"
                  name="travellers"
                  value={form.travellers}
                  onChange={handleChange}
                  min={1}
                  placeholder="e.g. 2"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full py-3 bg-brand-pink text-white rounded-xl font-medium hover:bg-brand-pink/90 transition-all shadow-lg shadow-brand-pink/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("Submitting...") : t("Submit Request")}
              </button>
              {success && <p className="text-green-500 text-sm">{success}</p>}
              {error && <p className="text-red-500 text-sm">{error}</p>}

            </form>
          </div>
        </div>
      )}

    </section>
  );
};

export default CTA;
